/**
 * TalentOS — API client and response types.
 *
 * Everything TalentOS-owned on the frontend lives under components/talentos/.
 * This file is the only place that talks to the TalentOS backend, so the whole
 * integration surface is two calls, both read-only.
 *
 * Reuses AIRecruit's exported `apiV2` axios instance rather than creating its
 * own: that instance carries the bearer-token request interceptor and the
 * single-flight refresh mutex. A separate instance would silently miss both,
 * and every TalentOS request would 401 the moment an access token expired
 * mid-session. TalentOS endpoints live under /api/v1 while that instance is
 * based at /api/v2, so each call passes an ABSOLUTE url — axios uses an
 * absolute url verbatim and ignores baseURL, while still running every
 * interceptor.
 */
import { apiV2, API_V1_URL } from '../../services/api'

const TALENTOS_BASE = `${API_V1_URL}/talentos`

// ── response types ──────────────────────────────────────────────────────────
// Mirror talentos/matching/api.py's Pydantic models exactly. Keep them in sync:
// the backend sets `extra="forbid"` on requests but responses are additive, so a
// new backend field is invisible here until it is added below.

/**
 * One row of the score breakdown.
 *
 * `weight_applied` splits these into two groups the report renders very
 * differently: `> 0` are the scored dimensions that compose the percentage,
 * `=== 0` are informational readings recorded for explainability that never
 * enter the sum.
 *
 * `display_reasoning` is MARKDOWN (`- ` bullets and `**bold**` only) produced
 * by a presentation pass, and must be rendered as markdown — printing it raw
 * shows literal asterisks. It falls back to `reasoning` server-side, so it is
 * only ever null when both are.
 */
export interface TalentosDimension {
  dimension_key: string
  raw_score: number
  weight_applied: number
  reasoning: string | null
  display_reasoning: string | null
  evidence_for: string[]
  evidence_against: string[]
  probe: string | null
}

/** Who the run was about, as TalentOS parsed them — NOT from AIRecruit's own
 *  parser. The years here are what the eligibility gate was evaluated against,
 *  so they always agree with the gate verdict shown beside them.
 *
 *  `current_company`/`location` added 2026-09-23 (matching-report field
 *  audit) — optional for the same two-pipeline reason as the v0.11 gate
 *  fields below: this bundle can run against a backend that predates them. */
export interface TalentosCandidate {
  full_name: string | null
  current_title: string | null
  current_company?: string | null
  location?: string | null
  /** The résumé's own inferred domain/industry list. NOT the same value the
   *  `domain` scoring dimension judges against — that dimension only emits a
   *  qualitative band, never a resume-side domain string of its own. Added
   *  2026-09-23 (comparison-table field audit). */
  domain?: string[] | null
  total_experience_years: number | null
  /** The résumé's free-text education lines (real, already-populated
   *  ResumeAnalysis.education_summary) -- NOT the structured
   *  education_entries column, which stays genuinely empty today. Added
   *  2026-10-08 for the report's Education card. */
  education_summary?: string[] | null
}

/** The JD side of the run, as TalentOS parsed it — mirrors
 *  `TalentosCandidate` for the résumé side. Added 2026-09-23. Optional on
 *  `TalentosMatchRun` for the same two-pipeline reason as everything else
 *  added that day.
 *
 *  `min_experience_years`/`max_experience_years` added 2026-09-23
 *  (comparison-table field audit) — the JD's own stated range, the same
 *  input the experience gate was evaluated against. */
/** One acceptable education path a JD states -- shape per migration
 *  ed5f74c66aa7's own docstring. Column is mapped and wired through to the
 *  report now, but no JD-analysis extraction writes it yet (2026-10-08), so
 *  this is always an empty array in practice until that's built. */
export interface TalentosEducationRequirement {
  requirement_type: string
  minimum_degree_level: string | null
  preferred_fields_of_study: string[] | null
  equivalent_experience_accepted: boolean
  rationale: string | null
}

export interface TalentosJob {
  role_title: string | null
  seniority_level: string | null
  domain: string | null
  min_experience_years?: number | null
  max_experience_years?: number | null
  /** Added 2026-10-08 for the report's Education card -- see
   *  TalentosEducationRequirement's own docstring for why it's always empty
   *  today. */
  education_requirements?: TalentosEducationRequirement[] | null
}

/** One JD responsibility's coverage row — the comparison table's
 *  per-Responsibility rows. Added 2026-09-23 (comparison-table field audit).
 *  `label` is TalentOS's own per-item verdict, shown verbatim (see
 *  `responsibilityLabelText` in talentosLabels) -- there is no numeric
 *  percentage stored per item, only the aggregate `responsibility_alignment`
 *  dimension score, so the report never invents one for this cell. */
export type TalentosResponsibilityLabel = 'covered' | 'partial' | 'not_evidenced'

export interface TalentosResponsibilityComparison {
  responsibility: string
  label: string
  citation: string | null
}

/** One JD skill (required or preferred), matched or missing, for the
 *  "JD vs Résumé Comparison" table. Added 2026-09-23 — built entirely from
 *  data TalentOS already computes when scoring `primary_skills`/
 *  `secondary_skills`; `verdict` is the one derived field (a plain mapping
 *  off `corroboration_status`, done for display only — see talentosLabels
 *  for the human-facing label/color). */
export type TalentosSkillVerdict = 'matched' | 'partial' | 'not_found' | 'insufficient_evidence'

export interface TalentosSkillComparison {
  skill: string
  category: 'required' | 'preferred'
  minimum_years: number | null
  corroboration_status: string | null
  verdict: TalentosSkillVerdict
  evidence: string
  proficiency_label?: string | null
  years_of_experience?: number | null
  evidence_snippet?: string | null
}

/** Preferred/secondary-skill coverage — same shape as
 *  `TalentosSkillsThresholdEvidence` below, minus `threshold`: preferred
 *  skills are never gated (only required skills are), so there is no
 *  pass/fail threshold behind this count, just what TalentOS evidenced. */
export interface TalentosPreferredSkillsEvidence {
  matched_count: number
  jd_skill_count: number
  coverage: number | null
  missing_skills: string[]
}

export type TalentosGateStatus = 'ineligible' | 'qualified' | 'over_qualified'

/**
 * The 3-tier scheme, plus the two 4-tier values it replaced.
 *
 * Migration d8f31c6b85a4 rewrites stored values -- 'strong_hire' becomes
 * 'strongly_recommended' and 'consider_with_caution' is folded into
 * 'recommended' or 'not_recommended'. The legacy members are kept because the
 * frontend and the backend deploy through separate pipelines, so this bundle
 * can be live against a database where that migration has not run yet. Without
 * them, every verdict pill in that window renders blank.
 *
 * They are read-only history: nothing emits them after the migration, and
 * NOTHING new should ever produce one.
 */
export type TalentosRecommendation =
  | 'strongly_recommended'
  | 'recommended'
  | 'not_recommended'
  | 'strong_hire'
  | 'consider_with_caution'

/** Both v0.11 gates report one of three outcomes. `skipped` is a real
 *  result, not a placeholder: the required-skills check never runs when the
 *  mandatory-item check has already failed. Render it as an absence of a
 *  finding — never as a second failure. */
export type TalentosGateOutcome = 'passed' | 'failed' | 'skipped'

/** One mandatory item and how it was judged. `rationale` has two very
 *  different provenances: for a skill-typed item it is one of three fixed
 *  strings from the backend's deterministic check, while for a
 *  certification, domain or other item it is free prose written by the
 *  model. Both are safe to display; only the latter varies run to run. */
export interface TalentosMandatoryItem {
  type: string
  value: string
  matched: boolean
  rationale: string
}

/** One of the JD's own up-to-3 mandatory items, "for reference" -- distinct
 *  from `TalentosMandatoryItem` above: this is read straight off the JD
 *  analysis (`mandatory_gate_evidence`'s source data BEFORE any gate
 *  evaluation), so it has no `matched` -- while the gate stays disabled
 *  (2026-09-10), nothing has judged these one way or the other. Added
 *  2026-09-23. */
export interface TalentosMandatoryRequirementItem {
  type: string
  value: string
  rationale: string | null
}

/** The required-skills check as structured values rather than as the
 *  pre-composed sentence in `skills_threshold_reason`.
 *
 *  Prefer this over that sentence for anything a recruiter reads. The
 *  sentence is the engine's audit record and names the threshold applied —
 *  a number no recruiter, role, or job description ever set — whereas these
 *  fields carry what the reader can act on, including the NAMES of the
 *  skills not evidenced, which the sentence never carried at all.
 *
 *  `threshold` is present for completeness and traceability. It is not
 *  intended for display. */
export interface TalentosSkillsThresholdEvidence {
  matched_count: number
  jd_skill_count: number
  coverage: number
  threshold: number
  missing_skills: string[]
}

/** The full report payload. `overall_score`, `display_percent` and
 *  `recommendation` are ALL null when `gate_status === 'ineligible'`: such a
 *  candidate was never scored, which is different from scoring badly. */
export interface TalentosMatchRun {
  match_run_id: string
  job_id: string
  host_resume_id: string | null
  gate_status: TalentosGateStatus
  gate_reason: string | null
  /** The v0.11 gates. Distinct from `gate_status`/`gate_reason` above, which
   *  is the older experience gate. Either of these failing forces
   *  `recommendation` to 'not_recommended' regardless of `display_percent`,
   *  so a high score beside a negative verdict is correct, not a bug.
   *
   *  OPTIONAL, and the `?` is a deployment fact rather than a modelling
   *  nicety: frontend and backend ship through separate pipelines, so this
   *  bundle can run against a v0.10 API that does not return these fields at
   *  all. Declaring them required asserted "my backend is at least v0.11",
   *  which is exactly the assumption a two-pipeline deploy cannot make.
   *  `undefined` means "this backend does not report gates"; `null` on the
   *  reason/evidence members means "it reports them, and recorded nothing".
   *  Use `hasGateData()` in talentosLabels to tell the two apart. */
  mandatory_gate_status?: TalentosGateOutcome
  mandatory_gate_reason?: string | null
  mandatory_gate_evidence?: TalentosMandatoryItem[] | null
  skills_threshold_status?: TalentosGateOutcome
  skills_threshold_reason?: string | null
  skills_threshold_evidence?: TalentosSkillsThresholdEvidence | null
  overall_score: number | null
  display_percent: number | null
  recommendation: TalentosRecommendation | null
  confidence_score: number | null
  hitl_required: boolean
  narrative_summary: string | null
  candidate: TalentosCandidate
  /** Added 2026-09-23 (matching-report field audit) — optional for the same
   *  two-pipeline reason as the v0.11 gate fields above. */
  job?: TalentosJob
  mandatory_requirements?: TalentosMandatoryRequirementItem[] | null
  preferred_skills_evidence?: TalentosPreferredSkillsEvidence | null
  weighted_sum?: number | null
  coherence_multiplier?: number | null
  skill_comparison?: TalentosSkillComparison[]
  responsibility_comparison?: TalentosResponsibilityComparison[]
  created_at: string
  scoring_engine_version: string
  dimensions: TalentosDimension[]
}

/** The headline result for one candidate, for list rendering. */
export interface TalentosMatchSummary {
  host_resume_id: string
  match_run_id: string
  gate_status: TalentosGateStatus
  /** Statuses only — the list needs to know a gate decided the verdict, not
   *  why. The reasons and evidence are one report away. Without these a row
   *  renders a confident percentage for a candidate the engine rejected.
   *
   *  Optional for the same two-pipeline reason as on TalentosMatchRun above. */
  mandatory_gate_status?: TalentosGateOutcome
  skills_threshold_status?: TalentosGateOutcome
  display_percent: number | null
  recommendation: TalentosRecommendation | null
  hitl_required: boolean
  created_at: string
}

export interface TalentosMatchSummaryList {
  job_id: string
  results: TalentosMatchSummary[]
}

/** Mirrors talentos/resume_analysis/api.py's ResumeSkillOut exactly. */
export interface TalentosResumeSkill {
  canonical_name: string
  category: string
  proficiency_label: string | null
  years_of_experience: number | null
  last_used_year: number | null
  corroboration_status: string
  specificity_score: number | null
  evidence_snippet: string | null
  extraction_confidence: number | null
}

/** Mirrors talentos/resume_analysis/api.py's AchievementOut exactly. */
export interface TalentosAchievement {
  statement_text: string
  is_quantified: boolean
  quantified_metric: string | null
  has_plausible_context: boolean
  specificity_score: number | null
  impact_score: number | null
  linked_skill_ids: string[]
}

/** Mirrors talentos/resume_analysis/api.py's CareerHistoryEntryOut exactly. */
export interface TalentosCareerHistoryEntry {
  company_name: string | null
  title: string | null
  normalized_title: string | null
  seniority_rank: number | null
  start_date: string | null
  end_date: string | null
  duration_months: number | null
  sequence_order: number
  description_summary: string | null
  achievements: TalentosAchievement[]
}

/** Mirrors talentos/resume_analysis/api.py's QualitySignalsOut exactly. */
export interface TalentosQualitySignals {
  skill_corroboration_rate: number | null
  generic_language_ratio: number | null
  quantified_achievement_rate: number | null
  keyword_stuffing_flag: boolean
  overall_authenticity_score: number | null
  skills_claimed_count: number | null
  skills_evidenced_count: number | null
  skills_claimed_without_evidence: number | null
  skills_evidenced_not_claimed: number | null
  computed_at: string
}

/**
 * Mirrors talentos/resume_analysis/api.py's ResumeAnalysisResponse exactly --
 * deliberately exhaustive (every talentos_resume_analyses column), backing
 * the "pure TalentOS" verification view (2026-09-30), not a curated subset.
 */
export interface TalentosResumeAnalysis {
  resume_analysis_id: string
  organization_id: string
  candidate_id: string
  candidate_full_name: string | null
  candidate_email: string | null
  candidate_phone: string | null
  full_name: string | null
  current_title: string | null
  current_company: string | null
  location: string | null
  total_experience_years: number | null
  certifications: string[]
  domain: string[]
  publications: string[]
  other_qualifications: string[]
  education_summary: string[]
  profile_quality_score: number | null
  skills: TalentosResumeSkill[]
  career_history: TalentosCareerHistoryEntry[]
  quality_signals: TalentosQualitySignals | null
  source_file_reference: string | null
  resume_document_hash: string
  parser_model_version: string
  embedding_model_version: string | null
  extraction_status: string
  created_at: string
  created_by: string
  raw_extracted_text: string | null
}

/** Mirrors talentos/jd_analysis/api.py's JdSkillOut exactly. */
export interface TalentosJdSkill {
  canonical_name: string
  requirement_type: string
  weight: number | null
  category: string
  minimum_years: number | null
  /** How this skill is used in this role. */
  context: string | null
  /** Why this skill's weight (not the requirement itself). */
  rationale: string | null
}

/** Mirrors talentos/jd_analysis/api.py's JdResponsibilityOut exactly. */
export interface TalentosJdResponsibility {
  responsibility_text: string
  criticality: string
}

/** One entry of JdAnalysisResponse.mandatory_requirements["items"] -- NOT the
 *  same shape as TalentosMandatoryRequirementItem above (that one is the
 *  match-report's mandatory_gate_evidence, a different endpoint). This one
 *  mirrors backend/talentos/jd_analysis/mandatory_requirements_pure.py::
 *  build_mandatory_requirements_payload exactly, not modeled further on the
 *  backend side (JdAnalysisResponse.mandatory_requirements stays a raw
 *  dict), so this type is sourced directly from that function. */
export interface TalentosJdMandatoryRequirementItem {
  type: 'skill' | 'certification' | 'domain' | 'other'
  value: string
  skill_id: string | null
  source: string
  rationale: string
}

/** Shape of JdAnalysisResponse.mandatory_requirements as a whole -- see
 *  TalentosJdMandatoryRequirementItem's own docstring for where this comes
 *  from. */
export interface TalentosJdMandatoryRequirements {
  items: TalentosJdMandatoryRequirementItem[]
  derivation: string
  model_version: string
}

/**
 * Mirrors talentos/jd_analysis/api.py's JdAnalysisResponse exactly --
 * deliberately exhaustive (every talentos_jd_analyses column), same "pure
 * TalentOS" verification treatment as TalentosResumeAnalysis.
 */
export interface TalentosJdAnalysis {
  jd_analysis_id: string
  job_id: string | null
  organization_id: string
  role_title: string | null
  seniority_level: string | null
  role_scope: string | null
  experience_range: string | null
  min_experience_years: number | null
  max_experience_years: number | null
  domain: string | null
  mandatory_requirements: TalentosJdMandatoryRequirements | null
  // Company Details (Job Overview tab). Null when the JD has no company
  // info, or for analyses run before these fields existed.
  // company_website always starts with http:// or https:// when present.
  company_website: string | null
  company_description: string | null
  // Education paths the JD asks for ([] when it names none).
  education_requirements: TalentosEducationRequirement[]
  skills: TalentosJdSkill[]
  responsibilities: TalentosJdResponsibility[]
  jd_text_hash: string
  parser_model_version: string
  embedding_model_version: string | null
  created_at: string
  created_by: string
  raw_jd_text: string | null
}

// ── calls ───────────────────────────────────────────────────────────────────

export const talentosApi = {
  /**
   * The full report for one candidate on one job.
   *
   * Resolves to `null` on 404 rather than rejecting. A candidate with no run is
   * an ordinary, expected state — anyone matched before TalentOS was installed
   * has none — so callers branch on `null` for "not scored yet" and reserve
   * their error state for genuine failures.
   */
  getLatestMatch: async (jobId: string, resumeId: string): Promise<TalentosMatchRun | null> => {
    try {
      const response = await apiV2.get<TalentosMatchRun>(`${TALENTOS_BASE}/match/latest`, {
        params: { job_id: jobId, resume_id: resumeId },
      })
      return response.data
    } catch (error: any) {
      if (error?.response?.status === 404) return null
      throw error
    }
  },

  /**
   * Every scored candidate on one job, in one round-trip.
   *
   * Used so a candidate list and the report opened from it show the same
   * number. Unscored candidates are simply absent from `results`; an unscored
   * job is a 200 with an empty array, never a 404.
   */
  getMatchesForJob: async (jobId: string): Promise<TalentosMatchSummaryList> => {
    const response = await apiV2.get<TalentosMatchSummaryList>(
      `${TALENTOS_BASE}/match/latest/by-job`,
      { params: { job_id: jobId } },
    )
    return response.data
  },

  /**
   * TalentOS's résumé analysis for an AIRecruit résumé, looked up by the
   * host `resume_id` -- see resume_analysis/api.py's by-host-resume route
   * docstring for why this needs a lookup rather than a direct id.
   * Resolves to `null` on 404: TalentOS may not have finished analysing
   * this résumé yet, same "not scored yet, not broken" convention as
   * `getLatestMatch`.
   */
  getResumeAnalysisByHostResume: async (resumeId: string): Promise<TalentosResumeAnalysis | null> => {
    try {
      const response = await apiV2.get<TalentosResumeAnalysis>(
        `${TALENTOS_BASE}/resume-analysis/by-host-resume/${resumeId}`,
      )
      return response.data
    } catch (error: any) {
      if (error?.response?.status === 404) return null
      throw error
    }
  },

  /**
   * TalentOS's JD analysis for an AIRecruit job, looked up by the host
   * `job_id` -- unlike résumé, `talentos_jd_analyses.job_id` points
   * straight at the host row, so this is a direct lookup server-side, not
   * a hash match. Resolves to `null` on 404, same convention as the other
   * two "not scored/analysed yet" calls.
   */
  getJdAnalysisByHostJob: async (jobId: string): Promise<TalentosJdAnalysis | null> => {
    try {
      const response = await apiV2.get<TalentosJdAnalysis>(
        `${TALENTOS_BASE}/jd-analysis/by-host-job/${jobId}`,
      )
      return response.data
    } catch (error: any) {
      if (error?.response?.status === 404) return null
      throw error
    }
  },
}
